import type { AccessLogFilter, IAccessLogRepository, PaginatedAccessLogs, PaginatedResponse } from '@yawi/domain';
import type { AccessLog } from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

// Tipos de respuesta de la API
interface ApiAccessLogResponse {
  id: string;
  timestamp: string;
  memberId?: string | null;
  userName: string;
  guestId?: string | null;
  method: string;
  entryPoint: string;
  status: string;
  details?: string | null;
  reason?: string | null;
  denialReason?: string | null;
  statusReason?: string | null;
  plateNumber?: string | null;
  photos?: string[];
  photoUrl?: string | null;
}

// Mapea la respuesta de la API al tipo AccessLog interno
const mapApiAccessLogToAccessLog = (apiLog: ApiAccessLogResponse): AccessLog => ({
  id: apiLog.id,
  timestamp: apiLog.timestamp,
  memberId: apiLog.memberId,
  userName: apiLog.userName,
  guestId: apiLog.guestId,
  method: apiLog.method,
  entryPoint: apiLog.entryPoint,
  status: apiLog.status,
  details: apiLog.details ?? apiLog.reason ?? apiLog.denialReason ?? apiLog.statusReason,
  plateNumber: apiLog.plateNumber,
  photos: apiLog.photos,
  photoUrl: apiLog.photoUrl ?? null,
});

export class ApiAccessLogRepository implements IAccessLogRepository {
  async getLogs(filter?: AccessLogFilter): Promise<AccessLog[]> {
    const client = getApiClient();

    const endpoint = filter?.userId
      ? 'v1/access-control/logs/me'
      : 'v1/access-control/logs';
    const params: Record<string, string | number> = {};
    if (filter?.method) params.method = Array.isArray(filter.method) ? filter.method.join(',') : filter.method;
    if (filter?.status) params.status = filter.status;
    if (filter?.startDate) params.dateFrom = filter.startDate;
    if (filter?.endDate) params.dateTo = filter.endDate;
    if (filter?.entryPoint) params.entryPoint = filter.entryPoint;
    if (filter?.search) params.search = filter.search;
    if (filter?.detailSearch) params.detailSearch = filter.detailSearch;

    const response = await client.get<PaginatedResponse<ApiAccessLogResponse>>(endpoint, { params });
    return response.data.map(mapApiAccessLogToAccessLog);
  }

  async getLogById(id: string): Promise<AccessLog | null> {
    // El API no tiene endpoint para obtener un log por ID
    // Obtener todos y filtrar
    const logs = await this.getLogs();
    const log = logs.find((l) => l.id === id);
    return log || null;
  }

  async createLog(log: Omit<AccessLog, 'id'>): Promise<AccessLog> {
    // El API no tiene endpoint para crear logs manualmente
    // Los logs se crean automáticamente por el sistema
    throw new Error('Logs are created automatically by the system');
  }

  async getRecentLogs(limit: number): Promise<AccessLog[]> {
    const logs = await this.getLogs();
    return logs.slice(0, limit);
  }

  async getLogsPaginated(
    filter?: AccessLogFilter,
    page: number = 1,
    pageSize: number = 20,
    signal?: AbortSignal,
  ): Promise<PaginatedAccessLogs> {
    const client = getApiClient();

    const endpoint = filter?.userId
      ? 'v1/access-control/logs/me'
      : 'v1/access-control/logs';
    const params: Record<string, string | number> = {
      page,
      limit: pageSize,
    };

    if (filter?.method) params.method = Array.isArray(filter.method) ? filter.method.join(',') : filter.method;
    if (filter?.status) params.status = filter.status;
    if (filter?.startDate) params.dateFrom = filter.startDate;
    if (filter?.endDate) params.dateTo = filter.endDate;
    if (filter?.entryPoint) params.entryPoint = filter.entryPoint;
    if (filter?.search) params.search = filter.search;
    if (filter?.detailSearch) params.detailSearch = filter.detailSearch;

    const response = await client.get<PaginatedResponse<ApiAccessLogResponse>>(endpoint, { params, signal });

    return {
      data: response.data.map(mapApiAccessLogToAccessLog),
      total: response.meta.total,
      page: response.meta.page,
      pageSize: response.meta.limit,
      hasMore: response.meta.hasNextPage,
    };
  }
}

